import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import styled, { ThemeProvider } from 'styled-components';
import { palette } from 'theme';
import rgba from 'polished/lib/color/rgba';
import _ from 'lodash';
import loadable from '@loadable/component';

import { withTheme } from '@material-ui/core/styles';
import Button from '@material-ui/core/Button';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import Divider from '@material-ui/core/Divider';
import Grid from '@material-ui/core/Grid';
import Paper from '@material-ui/core/Paper';
import Tab from '@material-ui/core/Tab';
import Tabs from '@material-ui/core/Tabs';
import Typography from '@material-ui/core/Typography';

const ContentTypography = loadable(() =>
  import('components/ContentTypography')
);
const HubspotForm = loadable(() => import('components/HubspotForm'));
const PaddedDivider = loadable(() => import('components/PaddedDivider'));
const Quote = loadable(() => import('components/Quote'));
const SectionGrid = loadable(() => import('components/Section'), {
  resolveComponent: components => components.SectionGrid
});
const SectionHeader = loadable(() => import('components/Section'), {
  resolveComponent: components => components.SectionHeader
});
const SlimRelatedItem = loadable(() => import('components/RelatedContent'), {
  resolveComponent: components => components.SlimRelatedItem
});

const SectionContent = styled.div`
  margin-bottom: 30px;
`;

const SubsectionPaper = styled(Paper)`
  margin-bottom: 18px;
  background: ${props =>
    props.theme.palette.type === 'dark' ? palette.grey[700] : palette.grey[50]};
`;

const SubsectionTabs = styled(Tabs)`
  border-bottom: 1px solid ${props => props.theme.palette.divider};
  color: ${props => props.theme.palette.text.primary};

  @media (max-width: 599px) {
    position: relative;
    &:after {
      content: '';
      border-radius: 5px;
      display: block;
      width: 20px;
      background: linear-gradient(
        to right,
        ${props =>
            rgba(
              props.theme.palette.type === 'dark'
                ? palette.grey[700]
                : palette.grey[50],
              0
            )}
          0%,
        ${props =>
            rgba(
              props.theme.palette.type === 'dark'
                ? palette.grey[700]
                : palette.grey[50],
              1
            )}
          100%
      );
      position: absolute;
      right: 0;
      top: 0;
      bottom: 2px;
      pointer-events: none;
    }
  }
`;

const TabContent = styled.div`
  display: ${props => (props.visible ? 'block' : 'none')};
  padding: 12px 24px;
`;

const PaddedQuote = styled(Quote)`
  margin-top: 24px;
`;

const ProductGrid = styled(Grid)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
`;

const SmallProductImg = styled.img`
  height: 40px;
  width: 40px;
  margin-right: 20px;
  flex-shrink: 0;
`;

const ProductButton = styled(Button)``;

const ProductInfoGrid = styled(Grid)`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  margin-bottom: 15x;
  @media (min-width: 960px) {
    flex-direction: row;
    margin: 0;
    justify-content: flex-start;
  }
`;

const ProductActionsGrid = styled(Grid)`
  display: flex;
  align-items: center;
  justify-content: center;
  ${ProductButton} {
    margin: 10px;
  }

  @media (min-width: 960px) {
    justify-content: flex-end;
  }
`;

const ApplicationSectionWrapper = ({ name, children }) => (
  <SectionGrid size="small" container>
    <Grid item xs={12}>
      <SectionHeader variant="overline">{name}</SectionHeader>
      {children}
    </Grid>
  </SectionGrid>
);

const ApplicationSubsections = withTheme()(({ subsections, theme }) => {
  const [activeTab, setActiveTab] = React.useState(0);

  function handleChange(event, newValue) {
    setActiveTab(newValue);
  }
  return (
    <ThemeProvider theme={theme}>
      <SubsectionPaper elevation={0}>
        <SubsectionTabs
          indicatorColor="primary"
          value={activeTab || 0}
          onChange={handleChange}
          variant="scrollable"
          scrollButtons="auto"
        >
          {subsections.map((subsection, idx) => (
            <Tab label={subsection.name} disableRipple key={idx} />
          ))}
        </SubsectionTabs>

        {subsections.map((subsection, idx) => (
          <TabContent visible={activeTab === idx} key={idx}>
            <ContentTypography
              dangerouslySetInnerHTML={{ __html: subsection.text }}
              variant="body2"
            />
            {subsection.product_references?.map((product, idx) => (
              <Fragment key={idx}>
                <PaddedDivider spacing={1} />
                <ProductGrid container>
                  <ProductInfoGrid item xs={12} md>
                    <SmallProductImg
                      src={product.icon?.url}
                      alt={product.name}
                    />
                    <Typography variant="h5" inline>
                      {product.name}
                    </Typography>
                  </ProductInfoGrid>
                  <ProductActionsGrid item xs={12} md>
                    {product.page_url && (
                      <ProductButton href={product.page_url} target="_blank">
                        Learn More
                      </ProductButton>
                    )}
                    {product.order_url && (
                      <ProductButton
                        href={product.order_url}
                        color="primary"
                        target="_blank"
                        variant="outlined"
                      >
                        Order
                      </ProductButton>
                    )}
                  </ProductActionsGrid>
                </ProductGrid>
              </Fragment>
            ))}
          </TabContent>
        ))}
      </SubsectionPaper>
    </ThemeProvider>
  );
});

const ApplicationSection = ({ section }) => (
  <>
    <ApplicationSectionWrapper name={section.name}>
      {section.content.map((item, idx) => (
        <SectionContent key={idx}>
          <Typography variant="h4" component="h2">
            {item.heading}
          </Typography>
          <ContentTypography
            dangerouslySetInnerHTML={{ __html: item.text }}
            variant="body2"
          />
        </SectionContent>
      ))}
      {section.subsections.length > 0 && (
        <ApplicationSubsections subsections={section.subsections} />
      )}

      {section.testimonials.map((testimonial, idx) => (
        <PaddedQuote quote={testimonial} size="small" key={idx} />
      ))}
    </ApplicationSectionWrapper>
  </>
);
const ApplicationRelatedSection = ({ section, label }) => (
  <ApplicationSectionWrapper name={label}>
    {section.map((block, idx) => (
      <Fragment key={idx}>
        {block.publication && (
          <SlimRelatedItem
            title={block.publication.publication_reference[0].title}
            label={`Publication - ${block.publication.publication_reference[0].journal}`}
            url={block.publication.publication_reference[0].link.href}
          />
        )}
        {block.enhanced_publication && (
          <SlimRelatedItem
            title={block.enhanced_publication.publication_reference[0].title}
            label={`Publication - ${_.get(
              block.enhanced_publication,
              'publication_reference[0].details.journal'
            ) ||
              _.get(
                block.enhanced_publication,
                'publication_reference[0].pubmed_data.fulljournalname'
              )}`}
            url={block.enhanced_publication.publication_reference[0].url}
          />
        )}
        {block.blog_post && (
          <SlimRelatedItem
            title={block.blog_post.blog_post_reference[0].title}
            label={`The Bench Blog`}
            url={block.blog_post.blog_post_reference[0].url}
            image={block.blog_post.blog_post_reference[0].hero_image}
          />
        )}
        {block.resource && (
          <SlimRelatedItem
            title={block.resource.resource_reference[0].title}
            label={`Resource - ${block.resource.resource_reference[0].resource_type}`}
            url={block.resource.resource_reference[0].url}
            image={block.resource.resource_reference[0].main_image}
          />
        )}
        {block.custom && (
          <SlimRelatedItem
            title={block.custom.title}
            label={block.custom.label}
            url={block.custom.url}
            image={block.custom.image}
          />
        )}
      </Fragment>
    ))}
  </ApplicationSectionWrapper>
);

const ApplicationContactSection = ({ section }) => (
  <ApplicationSectionWrapper name={section.name}>
    <SectionContent>
      <Typography variant="h4" component="h2">
        {section.content.heading}
      </Typography>
      <ContentTypography
        dangerouslySetInnerHTML={{ __html: section.content.text }}
        variant="body2"
      />
      <PaddedDivider />
      <Card>
        {section.hubspot_form.form_label && (
          <Fragment>
            <CardContent>
              <Typography variant="h5" align="center">
                {section.hubspot_form.form_label}
              </Typography>
            </CardContent>
            <Divider />
          </Fragment>
        )}
        <CardContent>
          {section.hubspot_form.form_id && (
            <HubspotForm formId={section.hubspot_form.form_id} />
          )}
        </CardContent>
      </Card>
    </SectionContent>
  </ApplicationSectionWrapper>
);

ApplicationSection.propTypes = {
  section: PropTypes.object
};

ApplicationRelatedSection.propTypes = {
  section: PropTypes.array
};

ApplicationContactSection.propTypes = {
  section: PropTypes.object
};

export {
  ApplicationSection,
  ApplicationSubsections,
  ApplicationRelatedSection,
  ApplicationContactSection,
  ApplicationSectionWrapper
};
